import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';

import EventCard from '@components/asset/modal/tabs/workflow/attachment_details/versions_sidebar/event_card';
import ActionsMenu from '@components/asset/modal/tabs/workflow/attachment_details/versions_sidebar/actions_menu';
import NewComment from '@components/asset/modal/tabs/workflow/attachment_details/versions_sidebar/new_comment';
import { post as postAnnotations } from '@api/v4/versions/annotations';
import { deleteAnnotation } from '@api/v4/annotations/annotations';
import { extractAnnotation, updateAnnotations } from '@components/asset/modal/tabs/workflow/helper';
import { getMomentLocale } from '@helpers/locale';


const VersionCard = (props) => {
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showCommentsInput, setShowCommentsInput] = useState(false);
  const actionsMenuContainer = useRef();
  const newCommentContainer = useRef();
  const {
    activeVersion,
    annotationMode,
    checkedInStatus,
    commentsFeatureEnabled,
    initializeTab,
    isLatestVersion,
    setActiveVersion,
    setAnnotationsResponse,
    setShowCheckinCheckout,
    setTempAnnotation,
    setVersionsActionLoading,
    sidebarCard,
    updateFetchControllers,
    users,
  } = props;

  const handleToggleActionsMenu = (e) => {
    if (!actionsMenuContainer.current.contains(e.target)) {
      setShowActionsMenu(false);
    }
  };

  const handleToggleComments = (e) => {
    if (!newCommentContainer.current.contains(e.target)) {
      setShowCommentsInput(false);
    }
  };

  const addEventListeners = (container) => {
    if (container === 'actions') {
      window.addEventListener('click', handleToggleActionsMenu);
      window.addEventListener('keyup', handleToggleActionsMenu);
    }

    if (container === 'comments') {
      window.addEventListener('click', handleToggleComments);
    }
  };

  const removeEventListeners = (container) => {
    if (container === 'actions') {
      window.removeEventListener('click', handleToggleActionsMenu);
      window.removeEventListener('keyup', handleToggleActionsMenu);
    }

    if (container === 'comments') {
      window.removeEventListener('click', handleToggleComments);
    }
  };

  const handlePostAnnotations = ({ versionKey, note, parent_id }) => (
    postAnnotations({
      versionKey,
      note,
      params: { fields: 'version,parent' },
      ...parent_id && { parent_id },
    })
      .then((response) => {
        setAnnotationsResponse((prevState) => (updateAnnotations(response, prevState)));
      })
      .catch((err) => {
        console.log(err);
      })
  );

  const handleDeleteAnnotation = (annotationKey) => (
    deleteAnnotation({ annotationKey })
      .then(() => {
        setAnnotationsResponse((prevState) => (extractAnnotation(annotationKey, prevState)));
      })
      .catch((err) => {
        console.log(err);
      })
  );

  useEffect(() => {
    if (showActionsMenu) {
      addEventListeners('actions');
    } else {
      removeEventListeners('actions');
    }

    return () => removeEventListeners('actions');
  }, [showActionsMenu]);

  useEffect(() => {
    if (showCommentsInput) {
      addEventListeners('comments');
    } else {
      removeEventListeners('comments');
    }

    return () => removeEventListeners('comments');
  }, [showCommentsInput]);

  if (sidebarCard.type === 'reservation') {
    return (
      <EventCard
        key={sidebarCard.id}
        cardEvent={sidebarCard}
        users={users}
      />
    );
  }

  const { filename, small_thumbnail } = sidebarCard.version_details;

  // count down because of event order (newest first)
  let annotationNumber = sidebarCard.annotations.filter((annotation) => (annotation.type === 'annotation')).length;
  const handleAnnotationNumber = (eventType) => {
    const currentNumber = annotationNumber;
    if (eventType === 'annotation') {
      annotationNumber -= 1;
    }
    return currentNumber;
  };

  const timestamp = moment(sidebarCard.created_at).locale(getMomentLocale()).format('LL');
  const isActiveVersion = activeVersion.id === sidebarCard.id;

  return (
    <li className={`card ${isActiveVersion ? 'active' : ''}`}>
      <div className="header-wrapper">
        <button
          className="header"
          onClick={() => setActiveVersion({ type: sidebarCard.type, id: sidebarCard.id })}
          type="button"
        >
          <div className="header__image">
            <img src={small_thumbnail} alt="thumbnail" />
          </div>
          <div className="header__info">
            <p className="filename">{filename}</p>
            <p className="created-at">{`${t('Uploaded')} ${timestamp}`}</p>
          </div>
        </button>
        {commentsFeatureEnabled && (
          <div className="comments" ref={newCommentContainer}>
            <button
              className="comments__button"
              onClick={() => setShowCommentsInput((prevState) => (!prevState))}
              type="button"
            >
              {t('Leave a comment')}
            </button>
            <NewComment
              onSubmit={handlePostAnnotations}
              versionKey={sidebarCard.id}
              showCommentsInput={showCommentsInput}
              setShowCommentsInput={setShowCommentsInput}
            />
          </div>
        )}
        <div
          className={`actions ${showActionsMenu ? 'active' : ''}`}
          ref={actionsMenuContainer}
        >
          <button
            className="actions__button"
            onClick={() => setShowActionsMenu((prevState) => (!prevState))}
            type="button"
          >
            <span className="bff-ellipses" />
          </button>
          {showActionsMenu && (
            <ActionsMenu
              checkedInStatus={checkedInStatus}
              initializeTab={initializeTab}
              isLatestVersion={isLatestVersion}
              setShowActionsMenu={setShowActionsMenu}
              setShowCheckinCheckout={setShowCheckinCheckout}
              setVersionsActionLoading={setVersionsActionLoading}
              sidebarCard={sidebarCard}
              updateFetchControllers={updateFetchControllers}
            />
          )}
        </div>
      </div>
      <ul className="card__body">
        {Boolean(sidebarCard) && (
          sidebarCard.annotations.map((cardEvent) => (
            <EventCard
              key={cardEvent.id}
              annotationMode={annotationMode}
              annotationNumber={handleAnnotationNumber(cardEvent.type)}
              cardEvent={cardEvent}
              commentsFeatureEnabled={commentsFeatureEnabled}
              isActiveVersion={isActiveVersion}
              onDelete={handleDeleteAnnotation}
              onReply={handlePostAnnotations}
              setTempAnnotation={setTempAnnotation}
              users={users}
            />
          ))
        )}
      </ul>
    </li>
  );
};

VersionCard.propTypes = {
  activeVersion: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  annotationMode: PropTypes.bool.isRequired,
  checkedInStatus: PropTypes.oneOf(['checked_in', 'checked_out']).isRequired,
  commentsFeatureEnabled: PropTypes.bool.isRequired,
  initializeTab: PropTypes.func.isRequired,
  isLatestVersion: PropTypes.bool.isRequired,
  setActiveVersion: PropTypes.func.isRequired,
  setAnnotationsResponse: PropTypes.func.isRequired,
  setShowCheckinCheckout: PropTypes.func.isRequired,
  setTempAnnotation: PropTypes.func.isRequired,
  setVersionsActionLoading: PropTypes.func.isRequired,
  sidebarCard: PropTypes.shape({
    created_at: PropTypes.string,
    type: PropTypes.oneOf(['reservation', 'version']),
    id: PropTypes.string,
    version_details: PropTypes.shape({
      filename: PropTypes.string,
      small_thumbnail: PropTypes.string,
    }),
    annotations: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.oneOf(['annotation', 'comment']),
    })),
  }).isRequired,
  updateFetchControllers: PropTypes.func.isRequired,
  users: PropTypes.shape({}).isRequired,
};

export default VersionCard;
